import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, Button, EmptyState, Field, Input, Section, Select, Spinner, Switch, type Tone } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import type { HelpKey } from '@zt/help';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { bpsLabel, minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { ReferralSettings, ReferralSummary } from '../lib/types';
import { percentToBps } from './pricing';

/**
 * Pazarlama → Tavsiye programı (F6-14, ADR-095): üye kendi davet kodunu Hesabım'dan alıp paylaşır; arkadaş ilk
 * siparişinde indirim alır, sipariş teslim edilip bekleme süresi geçince davet edene tek kullanımlık ödül kuponu
 * verilir. Program varsayılan kapalıdır; mağaza kimseye mesaj göndermez.
 */

const CURRENCIES = ['TRY', 'EUR', 'USD', 'GBP', 'AED', 'SAR', 'AZN'];
const int = (s: string) => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : -1);
type Status = ReferralSummary['recent'][number]['status'];
const statusTone: Record<Status, Tone> = {
  pending: 'amber',
  rewarded: 'moss',
  cancelled: 'neutral',
  limit: 'slate',
};

type Reward = ReferralSettings['friend'];
interface RewardDraft {
  type: Reward['type'];
  value: string;
  min: string;
}
interface Draft {
  enabled: boolean;
  currency: string;
  friend: RewardDraft;
  referrer: RewardDraft;
  rewardValidDays: string;
  holdDays: string;
  maxRewards: string;
}

const rewardDraft = (r: Reward): RewardDraft => ({
  type: r.type,
  value: r.type === 'percentage' ? bpsLabel(r.value).replace('%', '') : minorToInput(r.value),
  min: minorToInput(r.minSubtotalMinor),
});

function draftOf(s: ReferralSettings): Draft {
  return {
    enabled: s.enabled,
    currency: s.currency,
    friend: rewardDraft(s.friend),
    referrer: rewardDraft(s.referrer),
    rewardValidDays: String(s.rewardValidDays),
    holdDays: String(s.holdDays),
    maxRewards: String(s.maxRewardsPerReferrer),
  };
}

const rewardBody = (r: RewardDraft, currency: string) => ({
  type: r.type,
  value: (r.type === 'percentage' ? percentToBps(r.value) : toMinor(r.value, currency)) ?? -1,
  minSubtotalMinor: r.min.trim() ? (toMinor(r.min, currency) ?? -1) : null,
});

function bodyOf(d: Draft) {
  return {
    enabled: d.enabled,
    currency: d.currency,
    friend: rewardBody(d.friend, d.currency),
    referrer: rewardBody(d.referrer, d.currency),
    rewardValidDays: int(d.rewardValidDays),
    holdDays: int(d.holdDays),
    maxRewardsPerReferrer: int(d.maxRewards),
  };
}

export function ReferralsPage() {
  const t = useT();
  const settings = useQuery({
    queryKey: ['referral-settings'],
    queryFn: () => api.get<ReferralSettings>('/v1/admin/referral/settings'),
  });
  const summary = useQuery({
    queryKey: ['referral-summary'],
    queryFn: () => api.get<ReferralSummary>('/v1/admin/referral/summary'),
  });
  if (!settings.data) return <Spinner label={t('common.loading')} />;
  return (
    <PageFrame helpKey="page.referrals">
      <div className="space-y-6">
        <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.referrals.lead')}</p>
        {summary.data && <SummarySection s={summary.data} />}
        <SettingsForm key={JSON.stringify(settings.data)} saved={settings.data} />
        {summary.data && <RecentSection s={summary.data} />}
      </div>
    </PageFrame>
  );
}

function SummarySection({ s }: { s: ReferralSummary }) {
  const t = useT();
  const stats = [
    { label: t('admin.referrals.codes'), value: s.codes },
    { label: t('admin.referrals.pending'), value: s.referrals.pending },
    { label: t('admin.referrals.rewarded'), value: s.referrals.rewarded },
    { label: t('admin.referrals.cancelled'), value: s.referrals.cancelled + s.referrals.limit },
    {
      label: t('admin.referrals.rewardCoupons'),
      value: t('admin.referrals.rewardCouponsValue', { issued: s.rewards.issued, used: s.rewards.used }),
    },
  ];
  return (
    <Section
      title={t('admin.referrals.summary')}
      action={
        <Badge tone={s.enabled ? 'moss' : 'neutral'}>
          {s.enabled ? t('admin.referrals.on') : t('admin.referrals.off')}
        </Badge>
      }
    >
      <dl className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5" aria-label={t('admin.referrals.summary')}>
        {stats.map((x) => (
          <div key={x.label} className="rounded-md border border-line bg-surface px-3 py-2">
            <dt className="text-xs text-ink-3">{x.label}</dt>
            <dd className="mt-0.5 text-[0.9375rem] font-medium tabular">{x.value}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

function RewardFields({
  who,
  r,
  currency,
  onChange,
  err,
}: {
  who: 'friend' | 'referrer';
  r: RewardDraft;
  currency: string;
  onChange: (p: Partial<RewardDraft>) => void;
  err: (f: string) => string | undefined;
}) {
  const t = useT();
  return (
    <fieldset className="space-y-2 rounded-md border border-line p-3">
      <legend className="flex items-center gap-1 px-1 text-sm font-medium">
        {t(`admin.referrals.${who}`)}
        <HelpTip k={`referrals.${who}`} />
      </legend>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label={t('admin.referrals.rewardType')} help={<HelpTip k="referrals.reward_type" />}>
          {(p) => (
            <Select
              {...p}
              value={r.type}
              onChange={(e) => onChange({ type: e.target.value as RewardDraft['type'], value: '' })}
            >
              <option value="percentage">{t('admin.referrals.typePercent')}</option>
              <option value="fixed_amount">{t('admin.referrals.typeFixed', { currency })}</option>
            </Select>
          )}
        </Field>
        <Field
          label={
            r.type === 'percentage'
              ? t('admin.referrals.valuePercent')
              : t('admin.referrals.valueFixed', { currency })
          }
          help={<HelpTip k="referrals.reward_value" />}
          error={err(`${who}.value`)}
        >
          {(p) => (
            <Input
              {...p}
              inputMode="decimal"
              className="tabular"
              value={r.value}
              onChange={(e) => onChange({ value: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.referrals.minSubtotal', { currency })}
          help={<HelpTip k="referrals.reward_min" />}
          description={t('admin.referrals.minSubtotalHint')}
          error={err(`${who}.minSubtotalMinor`)}
        >
          {(p) => (
            <Input
              {...p}
              inputMode="decimal"
              className="tabular"
              value={r.min}
              onChange={(e) => onChange({ min: e.target.value })}
            />
          )}
        </Field>
      </div>
    </fieldset>
  );
}

function SettingsForm({ saved }: { saved: ReferralSettings }) {
  const t = useT();
  const { can } = usePermissions();
  const canManage = can('marketing.referral.manage');
  const [d, setD] = useState<Draft>(() => draftOf(saved));
  const set = (p: Partial<Draft>) => setD((x) => ({ ...x, ...p }));
  const dirty = JSON.stringify(d) !== JSON.stringify(draftOf(saved));
  const save = useApiMutation(() => api.put<ReferralSettings>('/v1/admin/referral/settings', bodyOf(d)), {
    invalidate: [['referral-settings'], ['referral-summary']],
    success: t('admin.referrals.saved'),
    silentFieldErrors: true,
  });
  const err = (f: string) => fieldError(save.error, f);
  const turningOn = d.enabled && !saved.enabled;
  const num = (k: 'rewardValidDays' | 'holdDays' | 'maxRewards', field: string, help: HelpKey) => (
    <Field label={t(`admin.referrals.${k}`)} help={<HelpTip k={help} />} error={err(field)}>
      {(p) => (
        <Input {...p} inputMode="numeric" value={d[k]} onChange={(e) => set({ [k]: e.target.value })} />
      )}
    </Field>
  );
  return (
    <Section title={t('admin.referrals.settings')} description={t('admin.referrals.settingsLead')}>
      <fieldset disabled={!canManage} className="grid max-w-3xl gap-4">
        <div className="flex items-center gap-2">
          <Switch
            id="referral-enabled"
            checked={d.enabled}
            onCheckedChange={(v) => set({ enabled: v })}
            disabled={!canManage}
          />
          <label htmlFor="referral-enabled" className="text-sm font-medium">
            {t('admin.referrals.enabled')}
          </label>
          <HelpTip k="referrals.enabled" />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t('admin.referrals.currency')} help={<HelpTip k="referrals.currency" />}>
            {(p) => (
              <Select {...p} value={d.currency} onChange={(e) => set({ currency: e.target.value })}>
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <RewardFields
          who="friend"
          r={d.friend}
          currency={d.currency}
          onChange={(p) => set({ friend: { ...d.friend, ...p } })}
          err={err}
        />
        <RewardFields
          who="referrer"
          r={d.referrer}
          currency={d.currency}
          onChange={(p) => set({ referrer: { ...d.referrer, ...p } })}
          err={err}
        />
        <div className="grid gap-3 sm:grid-cols-3">
          {num('holdDays', 'holdDays', 'referrals.hold_days')}
          {num('rewardValidDays', 'rewardValidDays', 'referrals.valid_days')}
          {num('maxRewards', 'maxRewardsPerReferrer', 'referrals.max_rewards')}
        </div>
        {save.error && !save.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {save.error.title}
          </p>
        )}
        {save.error && save.error.errors.length > 0 && (
          <p role="alert" className="text-sm text-rose">
            {t('admin.referrals.fixErrors')}
          </p>
        )}
      </fieldset>
      {canManage && (
        <div className="mt-4 flex items-center gap-1">
          {turningOn ? (
            <ConfirmButton
              variant="primary"
              size="md"
              disabled={!dirty}
              message={t('admin.referrals.enableConfirm')}
              onConfirm={() => save.mutateAsync(undefined).catch(() => undefined)}
            >
              {t('admin.referrals.save')}
            </ConfirmButton>
          ) : (
            <Button
              variant="primary"
              disabled={!dirty}
              loading={save.isPending}
              onClick={() => save.mutate(undefined)}
            >
              {t('admin.referrals.save')}
            </Button>
          )}
          <HelpTip k="referrals.save" />
        </div>
      )}
    </Section>
  );
}

function RecentSection({ s }: { s: ReferralSummary }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  return (
    <Section title={t('admin.referrals.recent')} description={t('admin.referrals.recentLead')}>
      {!s.recent.length ? (
        <EmptyState title={t('admin.referrals.emptyTitle')} description={t('admin.referrals.emptyLead')} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[44rem] text-sm" aria-label={t('admin.referrals.recent')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.referrals.colDate')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.referrals.colReferrer')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.referrals.colOrder')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.referrals.colStatus')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.referrals.colReward')}</th>
              </tr>
            </thead>
            <tbody>
              {s.recent.map((r) => (
                <tr key={r.id} className="border-t border-line align-top">
                  <td className="px-3 py-2 text-ink-2">{formatDate(r.createdAt, true)}</td>
                  <td className="px-3 py-2">
                    <Link
                      to="/musteriler/$id"
                      params={{ id: r.referrerId }}
                      className="text-pine underline underline-offset-2 hover:no-underline"
                    >
                      {r.referrerName}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to="/siparisler/$id"
                      params={{ id: r.orderId }}
                      className="text-pine underline underline-offset-2 hover:no-underline"
                    >
                      {r.orderNumber}
                    </Link>
                    <span className="block text-ink-3 tabular">{money(r.orderTotalMinor, r.currency)}</span>
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={statusTone[r.status]}>{t(`admin.referrals.statuses.${r.status}`)}</Badge>
                    {r.resolvedAt && <span className="block text-ink-3">{formatDate(r.resolvedAt)}</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[0.8125rem]">{r.rewardCode ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
